// src/encounter/smart-phrase-editor.ts
//
// Applies saved phrases and their pieces to the live Quill instance inside the Signal DS
// `RichTextEditor` (spec §4, §6): the `.trigger` list, insertion from it or from the Macro menu,
// moving between pieces (F2 / Shift+F2, and Tab / Shift+Tab on a piece), each piece's control
// (choose, fill, refresh, make plain text), and the clipboard rule.
//
// WHY IT REACHES QUILL DIRECTLY. The DS molecule is a controlled Delta editor with no cursor API;
// every one of these behaviours needs the cursor. Quill registers each editor against its
// container (`Quill.find`), so the instance is recovered from the DS wrapper's DOM. Every mutation
// goes through Quill with source `user` between history cut-offs, so it is ONE undo unit and flows
// back out through the DS `onChange` — the dialog's draft never diverges from the editor.
//
// INDEXES. Quill's `getText()` drops embeds (a piece, an image), which would shift every index
// after one, so this file reads the document as its ops with one U+FFFC per embed.

import { useEffect, useEffectEvent, useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent, type RefObject } from 'react';
import { Quill } from 'react-quill-new';
import type HistoryType from 'quill/modules/history';
import type { RichTextOp } from '@scripsteam/scrips-signal-ds';
import './note-piece-blot';
import { matchMacros, opsLength, phraseInsertOps, phraseTriggerAt, type NoteMacro, type PhraseTrigger } from './lib/smart-phrase';
import {
  PIECE_EMBED,
  choosePick,
  embeddedPiece,
  encodeNoteOps,
  exportPlainText,
  fillBlank,
  isPieceOpen,
  pieceDisplay,
  type ChartQuery,
  type ChartSnapshot,
  type Piece,
} from './lib/note-pieces';
import { resolveChart, sameChartContent, type ChartReads } from './lib/to-chart-snapshot';
import type { DeltaOp } from './lib/note-delta';

type QuillEditor = InstanceType<typeof Quill>;
interface EditorRange {
  index: number;
  length: number;
}

/** What the strip under the editor is showing, if anything. */
export type SmartPhrasePopup =
  | { mode: 'phrase'; trigger: PhraseTrigger; items: NoteMacro[]; active: number; patientId?: string; encounterId?: string }
  | { mode: 'piece'; index: number; piece: Piece; active: number };

/** Where a chart piece's Refresh stands (R17 / R18). */
export type ChartRefreshState =
  | { kind: 'checking' }
  | { kind: 'unchanged'; at: string }
  | { kind: 'changed'; next: ChartSnapshot; patientId?: string; encounterId?: string }
  | { kind: 'refused' }
  | { kind: 'failed' };

/** The Quill instance the DS RichTextEditor mounted inside `wrapper`. */
export function findQuill(wrapper: HTMLElement | null): QuillEditor | null {
  const container = wrapper?.querySelector('.ql-container');
  if (!container) return null;
  const found = Quill.find(container);
  return found instanceof Quill ? found : null;
}

/** The document as index-true text (one U+FFFC per embed). */
export function editorText(quill: QuillEditor): string {
  return quill
    .getContents()
    .ops.map((op) => (typeof op.insert === 'string' ? op.insert : '￼'))
    .join('');
}

/** Every piece in the document with its index. */
export function piecesIn(quill: QuillEditor): Array<{ index: number; piece: Piece }> {
  const out: Array<{ index: number; piece: Piece }> = [];
  let index = 0;
  for (const op of quill.getContents().ops) {
    const piece = embeddedPiece(op as DeltaOp);
    if (piece) out.push({ index, piece });
    index += typeof op.insert === 'string' ? op.insert.length : 1;
  }
  return out;
}

function history(q: QuillEditor): HistoryType | undefined {
  return q.getModule('history') as HistoryType | undefined;
}

export interface SmartPhraseEditorOptions {
  /** The DS RichTextEditor wrapper (its forwarded ref). */
  editorRef: RefObject<HTMLDivElement | null>;
  macros: NoteMacro[];
  /** What chart pieces are resolved from — the page's already-loaded reads. */
  chartReads?: ChartReads;
}

export function useSmartPhraseEditor({ editorRef, macros, chartReads }: SmartPhraseEditorOptions) {
  const [popup, setPopup] = useState<SmartPhrasePopup | null>(null);
  // The last non-null selection — the Macro menu takes focus away from the editor, and its phrase
  // still has to land where the cursor was.
  const lastRange = useRef<EditorRange | null>(null);
  // Set while this hook is itself changing the document, so its own edits never re-open the strip.
  const applying = useRef(false);
  // The trigger the clinician dismissed with Esc — not offered again until they start a new one.
  const dismissedAt = useRef<number | null>(null);

  const quill = () => findQuill(editorRef.current);
  const chart = (q: ChartQuery) => resolveChart(q, chartReads);

  // ─── Selection contract (§4.1) ─────────────────────────────────────────────────────────────────
  // Opening the list captures the editor, the note, the selection and the patient + encounter; an
  // insertion validates all of them first and aborts if any changed, so a phrase can never land in
  // a note, at a place, or for a patient the clinician did not pick it for.
  const [notice, setNotice] = useState<string | null>(null);
  const contract = useRef<{
    quill: QuillEditor;
    text: string;
    range: EditorRange | null;
    patientId?: string;
    encounterId?: string;
  } | null>(null);
  const NOTE_CHANGED = 'Note changed; insert again';
  const samePatient = (ids: { patientId?: string; encounterId?: string }) =>
    ids.patientId === chartReads?.patientId && ids.encounterId === chartReads?.encounterId;
  const captureContract = () => {
    const q = quill();
    const r = lastRange.current;
    contract.current = q
      ? { quill: q, text: editorText(q), range: r ? { ...r } : null, patientId: chartReads?.patientId, encounterId: chartReads?.encounterId }
      : null;
    setNotice(null);
  };
  const contractHolds = (q: QuillEditor): boolean => {
    const c = contract.current;
    const r = lastRange.current;
    return (
      !!c &&
      c.quill === q &&
      c.text === editorText(q) &&
      (c.range?.index ?? null) === (r?.index ?? null) &&
      (c.range?.length ?? null) === (r?.length ?? null) &&
      samePatient(c)
    );
  };

  /** Is this event's target inside the note's editable root (not the toolbar or the link box)? */
  const inNote = (target: EventTarget | null): boolean => {
    const q = quill();
    return !!q && target instanceof Node && q.root.contains(target);
  };

  /** One undo unit: history cut-offs on both sides of the change. */
  const transact = (q: QuillEditor, fn: () => void) => {
    applying.current = true;
    history(q)?.cutoff();
    try {
      fn();
    } finally {
      history(q)?.cutoff();
      applying.current = false;
    }
  };
  const select = (q: QuillEditor, index: number, length: number) => {
    applying.current = true;
    try {
      q.setSelection(index, length, 'user');
    } finally {
      applying.current = false;
    }
    lastRange.current = { index, length };
  };

  const openPiece = (q: QuillEditor, index: number) => {
    // Leaving a blank for another piece commits what was typed in it (same length, so `index` holds).
    commitBlankDraft();
    const found = piecesIn(q).find((p) => p.index === index);
    if (!found) return;
    select(q, index, 1);
    setPopup({ mode: 'piece', index, piece: found.piece, active: -1 });
  };

  /** Move to the next (or previous) piece, wrapping. A selected piece counts as passed. */
  const jump = (direction: 'next' | 'previous') => {
    const q = quill();
    if (!q) return;
    const all = piecesIn(q);
    if (!all.length) return;
    const range = q.getSelection() ?? lastRange.current ?? { index: 0, length: 0 };
    const target =
      direction === 'next'
        ? (all.find((p) => p.index >= range.index + range.length) ?? all[0])
        : ([...all].reverse().find((p) => p.index < range.index) ?? all[all.length - 1]);
    openPiece(q, target.index);
  };

  /** After completing a piece: the next OPEN piece, else the caret just after this one. */
  const advance = (q: QuillEditor, index: number) => {
    const next = piecesIn(q).find((p) => p.index > index && isPieceOpen(p.piece));
    if (next) {
      openPiece(q, next.index);
      return;
    }
    select(q, index + 1, 0);
    setPopup(null);
  };

  const replacePiece = (q: QuillEditor, index: number, next: Piece) => {
    const ops: RichTextOp[] = [...(index > 0 ? [{ retain: index }] : []), { delete: 1 }, { insert: { [PIECE_EMBED]: next } }];
    transact(q, () => q.updateContents(ops, 'user'));
  };

  // ─── A blank's pending input ───────────────────────────────────────────────────────────────────
  // What the clinician types in a blank's input lives in that input until it is COMMITTED. Enter,
  // Fill, Tab / F2 commit it; so does everything that leaves the blank — Save, F2 from the note, a
  // click on another piece, Esc, Make plain text, the input losing focus. Without this, editing
  // "3 days" to "3 weeks" and pressing Save kept "3 days" in the saved note. An emptied input is an
  // explicit CLEAR, and commits as one (the blank re-opens).
  const blankDraft = useRef<{ pieceId: string; value: string } | null>(null);
  const draftBlank = (pieceId: string, value: string) => {
    blankDraft.current = { pieceId, value };
  };
  /** Write a blank's pending input into the note. True when the note changed. */
  const commitBlankDraft = (): boolean => {
    const draft = blankDraft.current;
    blankDraft.current = null;
    const q = quill();
    if (!draft || !q) return false;
    const found = piecesIn(q).find((p) => p.piece.id === draft.pieceId);
    if (!found || found.piece.t !== 'blank') return false;
    const next = fillBlank(found.piece, draft.value);
    if ((next.value ?? '') === (found.piece.value ?? '')) return false;
    replacePiece(q, found.index, next);
    return true;
  };

  /** Insert a saved phrase: over `replace` (the typed `.trigger`), else at the last cursor (over its
   *  selection), else at the end of the note. One undo unit; lands on the first open piece. */
  const insertPhrase = (macro: NoteMacro, replace?: EditorRange) => {
    const q = quill();
    if (!q) return;
    // The Macro menu path (no `replace`) is bound by the contract captured when the menu opened.
    if (!replace) {
      const holds = contractHolds(q);
      contract.current = null;
      if (!holds) {
        setNotice(NOTE_CHANGED);
        return;
      }
    }
    setNotice(null);
    const docEnd = Math.max(0, q.getLength() - 1);
    const target = replace ?? lastRange.current ?? { index: docEnd, length: 0 };
    const index = Math.min(target.index, docEnd);
    const remove = Math.min(target.length, docEnd - index);
    const { ops, block } = phraseInsertOps(macro.value, chart);
    const text = editorText(q);
    const needsBreak = block && index > 0 && text[index - 1] !== '\n';
    const body: DeltaOp[] = needsBreak ? [{ insert: '\n' }, ...ops] : ops;
    const delta = [
      ...(index > 0 ? [{ retain: index }] : []),
      ...(remove > 0 ? [{ delete: remove }] : []),
      ...body,
    ] as RichTextOp[];
    transact(q, () => q.updateContents(delta, 'user'));
    const end = index + opsLength(body);
    const first = piecesIn(q).find((p) => p.index >= index && p.index < end && isPieceOpen(p.piece));
    if (first) {
      openPiece(q, first.index);
      return;
    }
    select(q, end, 0);
    setPopup(null);
  };

  // ─── Piece controls ────────────────────────────────────────────────────────────────────────────
  const current = (): { q: QuillEditor; index: number; piece: Piece } | undefined => {
    const q = quill();
    if (!q || popup?.mode !== 'piece') return undefined;
    const found = piecesIn(q).find((p) => p.index === popup.index);
    return found ? { q, index: found.index, piece: found.piece } : undefined;
  };

  const choose = (code: string) => {
    const c = current();
    if (!c || c.piece.t !== 'pick') return;
    const next = choosePick(c.piece, code);
    replacePiece(c.q, c.index, next);
    if (next.multi) {
      select(c.q, c.index, 1);
      setPopup({ mode: 'piece', index: c.index, piece: next, active: popup?.active ?? -1 });
    } else advance(c.q, c.index);
  };

  /** Fill the blank, then move: to the next OPEN piece (default), or strictly next / previous. */
  const fill = (value: string, move: 'advance' | 'next' | 'previous' = 'advance') => {
    blankDraft.current = null; // the input's value is being committed right here
    const c = current();
    if (!c || c.piece.t !== 'blank') return;
    // An empty value CLEARS a filled blank (it re-opens); an unchanged value writes nothing.
    const next = fillBlank(c.piece, value);
    if ((next.value ?? '') !== (c.piece.value ?? '')) replacePiece(c.q, c.index, next);
    if (move === 'advance') {
      advance(c.q, c.index);
      return;
    }
    select(c.q, c.index, 1);
    jump(move);
  };

  // ─── Refresh (R17 fresh read, R18 same patient + encounter) ────────────────────────────────────
  const [refreshing, setRefreshing] = useState<{ pieceId: string; state: ChartRefreshState } | null>(null);
  // The page's CURRENT patient + encounter, for the continuation after the re-read's await. Kept in a
  // LAYOUT effect so it is current before the browser can deliver the next event (a passive effect
  // would leave a window where the ref still holds the previous page's ids).
  const liveIds = useRef<{ patientId?: string; encounterId?: string }>({});
  useLayoutEffect(() => {
    liveIds.current = { patientId: chartReads?.patientId, encounterId: chartReads?.encounterId };
  });
  const sameIds = (a: { patientId?: string; encounterId?: string } | undefined, b: { patientId?: string; encounterId?: string }) =>
    !!a && a.patientId === b.patientId && a.encounterId === b.encounterId;

  /** Re-read the chart for THIS page's patient and encounter. The note changes only on Update. */
  const refresh = async () => {
    const c = current();
    if (!c || c.piece.t !== 'chart') return;
    const piece = c.piece;
    const old = piece.snapshot;
    const pageIds = { patientId: chartReads?.patientId, encounterId: chartReads?.encounterId };
    const report = (state: ChartRefreshState) => setRefreshing({ pieceId: piece.id, state });
    const samePlace = (ids: { patientId?: string; encounterId?: string } | undefined) =>
      !!ids && ids.patientId === pageIds.patientId && ids.encounterId === pageIds.encounterId;
    // R18: a value pulled for another patient or encounter is never refreshed into this note.
    if (old && !samePlace(old)) return report({ kind: 'refused' });
    report({ kind: 'checking' });
    let reads: ChartReads | undefined;
    try {
      reads = chartReads?.refetch ? await chartReads.refetch() : chartReads;
    } catch {
      return report({ kind: 'failed' });
    }
    // After the await: the read must be for the ids we asked about AND the page must still be there.
    if (!samePlace(reads) || !sameIds(liveIds.current, pageIds)) return report({ kind: 'refused' });
    const next = resolveChart(piece.query, reads);
    // A failed or pending re-read never replaces a value that was read (R6).
    if (next.status === 'error' || next.status === 'loading') return report({ kind: 'failed' });
    if (old && sameChartContent(old, next)) return report({ kind: 'unchanged', at: next.pulledAt });
    report({ kind: 'changed', next, ...pageIds });
  };

  /** Apply a changed re-read: the ONLY path by which a chart piece's text and snapshot change. */
  const applyRefresh = () => {
    const q = quill();
    if (!q || refreshing?.state.kind !== 'changed') return;
    const found = piecesIn(q).find((p) => p.piece.id === refreshing.pieceId);
    if (!found || found.piece.t !== 'chart') return;
    const change = refreshing.state;
    // R18 at the moment of writing: the page is still the patient + encounter the re-read was for,
    // and so is the piece being replaced.
    // `chartReads` here is THIS render's props — the page as the clinician sees it when clicking.
    const pageNow = { patientId: chartReads?.patientId, encounterId: chartReads?.encounterId };
    if (
      !sameIds(pageNow, change) ||
      !sameIds(liveIds.current, change) ||
      (found.piece.snapshot && !sameIds(found.piece.snapshot, change))
    ) {
      setRefreshing({ pieceId: refreshing.pieceId, state: { kind: 'refused' } });
      return;
    }
    const next: Piece = { ...found.piece, snapshot: change.next };
    replacePiece(q, found.index, next);
    select(q, found.index, 1);
    setPopup({ mode: 'piece', index: found.index, piece: next, active: -1 });
    setRefreshing(null);
  };

  /** R2's escape hatch: the piece becomes ordinary narrative and ALL its metadata is dropped. */
  const makePlainText = (text?: string) => {
    commitBlankDraft(); // a blank becomes text with what was typed in it, not its old value
    const c = current();
    if (!c) return;
    const plain = (text ?? pieceDisplay(c.piece)).trim() || pieceDisplay(c.piece);
    const ops: RichTextOp[] = [...(c.index > 0 ? [{ retain: c.index }] : []), { delete: 1 }, { insert: plain }];
    transact(c.q, () => c.q.updateContents(ops, 'user'));
    select(c.q, c.index + plain.length, 0);
    setPopup(null);
  };

  const closePiece = () => {
    commitBlankDraft();
    const q = quill();
    if (q && popup?.mode === 'piece') select(q, popup.index + 1, 0);
    setPopup(null);
  };

  const acceptPhrase = (p: Extract<SmartPhrasePopup, { mode: 'phrase' }>, i: number) => {
    const item = p.items[i];
    const q = quill();
    if (!item || !q) return;
    // The `.trigger` path's contract: the typed trigger is still where the list found it, and the
    // patient + encounter are the ones the list was opened for.
    const { start, query } = p.trigger;
    if (editorText(q).slice(start, start + query.length + 1) !== `.${query}` || !samePatient(p)) {
      setPopup(null);
      setNotice(NOTE_CHANGED);
      return;
    }
    insertPhrase(item, { index: start, length: query.length + 1 });
  };

  // ─── Editor events ─────────────────────────────────────────────────────────────────────────────
  const onEditorChange = useEffectEvent((q: QuillEditor, source: unknown) => {
    if (applying.current || source !== 'user') return;
    const range = q.getSelection();
    if (!range) return;

    // Arriving on a piece (arrow keys, a click) opens its control; leaving it closes it.
    if (range.length === 1) {
      const onPiece = piecesIn(q).find((p) => p.index === range.index);
      if (onPiece) {
        if (popup?.mode !== 'piece' || popup.index !== onPiece.index) {
          setPopup({ mode: 'piece', index: onPiece.index, piece: onPiece.piece, active: -1 });
        }
        return;
      }
    }
    if (popup?.mode === 'piece') setPopup(null);

    const trigger = range.length === 0 ? phraseTriggerAt(editorText(q), range.index) : undefined;
    if (!trigger) {
      dismissedAt.current = null;
      if (popup?.mode === 'phrase') setPopup(null);
      return;
    }
    const items = dismissedAt.current === trigger.start ? [] : matchMacros(macros, trigger.query);
    setPopup(
      items.length
        ? { mode: 'phrase', trigger, items, active: 0, patientId: chartReads?.patientId, encounterId: chartReads?.encounterId }
        : null,
    );
  });

  const onPieceClick = useEffectEvent((q: QuillEditor, target: EventTarget | null) => {
    const node = target instanceof Element ? target.closest('.cps-piece') : null;
    if (!node) return;
    const blot = Quill.find(node);
    if (!blot || blot instanceof Quill) return;
    openPiece(q, q.getIndex(blot));
  });

  useEffect(() => {
    const q = findQuill(editorRef.current);
    if (!q) return;
    const handler = (event: string, arg: unknown, _old: unknown, source: unknown) => {
      if (event === 'selection-change' && arg) lastRange.current = arg as EditorRange;
      onEditorChange(q, source);
    };
    const click = (e: MouseEvent) => onPieceClick(q, e.target);
    q.on('editor-change', handler);
    q.root.addEventListener('click', click);
    return () => {
      q.off('editor-change', handler);
      q.root.removeEventListener('click', click);
    };
  }, [editorRef]);

  /** Capture-phase keys on the editor wrapper — they run BEFORE Quill's keyboard module, so a key
   *  that drives the strip never also edits the note, and Esc never also closes the dialog. */
  const onKeyDownCapture = (e: KeyboardEvent<HTMLElement>) => {
    // Only keys typed IN THE NOTE. The wrapper also holds Quill's own controls — the toolbar
    // pickers and the link box — whose typing must never be taken for the note's.
    if (!inNote(e.target)) return;
    const stop = () => {
      e.preventDefault();
      e.stopPropagation();
    };
    if (e.key === 'F2' || (e.key === 'Tab' && popup?.mode === 'piece')) {
      stop();
      jump(e.shiftKey ? 'previous' : 'next');
      return;
    }
    if (!popup) return;

    if (popup.mode === 'phrase') {
      const count = popup.items.length;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        stop();
        setPopup({ ...popup, active: (popup.active + (e.key === 'ArrowDown' ? 1 : count - 1)) % count });
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        stop();
        acceptPhrase(popup, popup.active);
      } else if (e.key === 'Escape') {
        stop();
        dismissedAt.current = popup.trigger.start;
        setPopup(null);
      }
      return;
    }

    // A piece is selected. Its text is owned by its control (R2): nothing typed edits it.
    const piece = popup.piece;
    if (e.key === 'Escape') {
      stop();
      closePiece();
      return;
    }
    if (e.key === 'Backspace' || e.key === 'Delete') return; // atomic: removes the whole piece
    if (e.key.startsWith('Arrow') && piece.t !== 'pick') return;
    if (piece.t === 'pick') {
      const count = piece.options.length;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        stop();
        // The highlight starts on NOTHING (R3: no option may look like a default).
        const from = popup.active < 0 ? (e.key === 'ArrowDown' ? -1 : 0) : popup.active;
        setPopup({ ...popup, active: (from + (e.key === 'ArrowDown' ? 1 : count - 1) + count) % count });
        return;
      }
      if (/^[1-9]$/.test(e.key) && Number(e.key) <= count) {
        stop();
        choose(piece.options[Number(e.key) - 1].code);
        return;
      }
      if (e.key === ' ' || (e.key === 'Enter' && !piece.multi)) {
        stop();
        const option = piece.options[popup.active];
        if (option) choose(option.code); // nothing highlighted → nothing chosen
        return;
      }
      if (e.key === 'Enter') {
        stop();
        const q = quill();
        if (q) advance(q, popup.index);
        return;
      }
    }
    if (piece.t === 'chart' && e.key === 'Enter') {
      stop();
      void refresh();
      return;
    }
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey) stop();
  };

  /** Paste — TONIGHT ALL PASTE IS PLAIN TEXT, pieces and formatting stripped: the conservative form
   *  of spec §6.6 (same-note paste keeping pieces with new ids is not built yet). A pasted piece
   *  can therefore never carry another patient's snapshot or a stale id. */
  const onPasteCapture = (e: ClipboardEvent<HTMLElement>) => {
    const q = quill();
    // A paste into Quill's link box (or any control that is not the note) is that control's own:
    // intercepting it would delete the note's selection and put the URL in the note.
    if (!q || !inNote(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    const text = e.clipboardData.getData('text/plain');
    const range = q.getSelection() ?? lastRange.current ?? { index: Math.max(0, q.getLength() - 1), length: 0 };
    // Nothing pasteable as text (an image, a file): change nothing — never delete the selection.
    if (!text) return;
    const ops: RichTextOp[] = [
      ...(range.index > 0 ? [{ retain: range.index }] : []),
      ...(range.length > 0 ? [{ delete: range.length }] : []),
      ...(text ? [{ insert: text }] : []),
    ];
    transact(q, () => q.updateContents(ops, 'user'));
    select(q, range.index + text.length, 0);
    setPopup(null);
  };

  /** Copy / cut put the selection's PLAIN-TEXT export on the clipboard (pieces as their display),
   *  so nothing piece-shaped leaves the note and a copied piece still reads correctly. */
  const onCopyCapture = (e: ClipboardEvent<HTMLElement>, cut = false) => {
    const q = quill();
    if (!q || !inNote(e.target)) return;
    const range = q.getSelection();
    if (!range || !range.length) return;
    e.preventDefault();
    e.stopPropagation();
    const ops = q.getContents(range.index, range.length).ops as DeltaOp[];
    e.clipboardData.setData('text/plain', exportPlainText(encodeNoteOps(ops)));
    if (cut) {
      transact(q, () => q.deleteText(range.index, range.length, 'user'));
      select(q, range.index, 0);
      setPopup(null);
    }
  };

  return {
    popup,
    setActive: (i: number) => setPopup((p) => (p ? { ...p, active: i } : p)),
    acceptPhrase: (i: number) => {
      if (popup?.mode === 'phrase') acceptPhrase(popup, i);
    },
    insertPhrase,
    /** Call when the Macro menu opens: captures what an insertion must still match. */
    captureContract,
    /** "Note changed; insert again" after an aborted insertion, else null. */
    notice,
    choose,
    fill,
    refresh,
    applyRefresh,
    /** The Refresh state of the ACTIVE piece, if it has one. */
    refreshState: popup?.mode === 'piece' && refreshing?.pieceId === popup.piece.id ? refreshing.state : null,
    makePlainText,
    closePiece,
    /** A blank's input changed: remember it, uncommitted, until something commits or leaves it. */
    draftBlank,
    /** Commit a blank's pending input now (its input lost focus). */
    commitBlank: () => {
      commitBlankDraft();
    },
    /**
     * Before Save: commit any pending blank input. Returns the note's ops as they now stand when that
     * changed the note (the dialog's draft state has not re-rendered yet), else undefined.
     */
    commitPending: (): DeltaOp[] | undefined => {
      if (!commitBlankDraft()) return undefined;
      return quill()?.getContents().ops as DeltaOp[] | undefined;
    },
    next: () => jump('next'),
    previous: () => jump('previous'),
    onKeyDownCapture,
    onPasteCapture,
    onCopyCapture: (e: ClipboardEvent<HTMLElement>) => onCopyCapture(e),
    onCutCapture: (e: ClipboardEvent<HTMLElement>) => onCopyCapture(e, true),
    /** Put the cursor back in the editor (after the Macro menu closes without a pick). */
    focusEditor: () => {
      const q = quill();
      if (!q) return;
      const r = lastRange.current;
      if (r) select(q, r.index, r.length);
      else q.focus();
    },
  };
}
